import {Hash} from "~/interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import IRepo from "~/interfaces/repo";
import Repo from "~/state/modules/repo";
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import FormBuilderSortable from "../formBuilderSortable";
import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
import {ObjectHelper} from "~/helpers/objectHelper";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import {RelationType} from "~/models/relationType";
import ObjectModel from "~/models/objectModel";
import Guid from "~/helpers/guid";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import {objectModelPrerequisiteType} from "~/enums/objectModelPrerequisiteType";
import RelativeTimePeriod from "~/models/relativeTimePeriod";
import {DurationType} from "~/enums/durationType";

export default class FormDetailObjectModelPrerequisite extends FormBuilderSortable implements IConditionalRuleContainer {
  _classKey = "formDetailObjectModelPrerequisite";
  object_model_client_id: string;
  object_model_prerequisite_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet>;
  prerequisite_type: number;
  error_message: string;

  static repo: IRepo<FormDetailObjectModelPrerequisite> = new Repo<FormDetailObjectModelPrerequisite>('formDetailObjectModelPrerequisite', FormDetailObjectModelPrerequisite, {});
  repo: IRepo<this>;
  relative_time_period: RelativeTimePeriod;

  initialize(form, sort_order): this {
    this.parent = form;
    this.defineProperty("client_id", Guid.create());
    this.defineProperty("object_model_client_id", "");
    this.defineProperty('sort_order', sort_order);
    this.defineProperty('prerequisite_type', objectModelPrerequisiteType.ConditionalRule )
    this.defineProperty('error_message', "");

    return this
  }

  constructor() {
    super();

    for (const definition of ObjectModelConditionalFields.FORM_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(
          this,
          definition,
          {},
          true,
          false
        );
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] =
          definition.defaultConditionalValue;
      }
    }

    if (!this.relative_time_period) {
      this.createEmbeddedObject.relative_time_period(this, {})
    }

  }

  get rootName(): string {
    return "object_model_prerequisites";
  }

  get classKey(): string {
    return this._classKey
  }

  get object_model(): ObjectModel {
    return ObjectModel.repo.find(this.object_model_client_id)
  }

  addToRepo(): void {
    FormDetailObjectModelPrerequisite.repo.add(this);
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.objectModelPrerequisiteHandler.recalculateElementPositions();
  }

  // Conditional rule set configuration

  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order - y.sort_order);
  }

  getDisplayName(prop_key): string{
    return prop_key
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    return this.object_model.objectModelPropertiesArray;
  }

  // Embedded models

  get EMBEDDED_MODELS(): { [key: string]: { type: any; relationType: RelationType }; } {
    const models = {};
    for (const definition of ObjectModelConditionalFields.FORM_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {
        type: ObjectModelConditionalRuleSet,
        relationType: RelationType.dictionary
      };
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {
          type: Object,
          relationType: RelationType.singleObject
        };
      }
    }
    models['relative_time_period']= {type: RelativeTimePeriod, relationType: RelationType.singleObject};

    return models;
  }

  createEmbeddedObject = {
    relative_time_period: (parent, json): RelativeTimePeriod => {
      let rtp = new RelativeTimePeriod(parent, 'relative_time_period', json);
      rtp.type = DurationType.propertyBasedTimeSpan;
      return rtp;
    }
  };
}